<template>
  <div>
    <app-grid-item
      :item="bible"
      :is-loading="isLoading"
      :is-rendering="isRendering"
      :menu-items="menuItems"
      item-type="bible"
    >
      <template #details>
        <template v-if="filesInfo">
          <app-grid-detail-item
            icon="file_copy"
            data-locator="size"
            :title="`${TRANSLATION_KEY_PREFIX}.filesInfo`"
          >
          <i18n-t :keypath="`${TRANSLATION_KEY_PREFIX}.filesInfoValue`" scope="global">
            <template v-slot:count>
              {{ bible.nbItems }}
            </template>
            <template v-slot:size>
              <app-filesize-display :value="bible.size" />
            </template>
          </i18n-t>
        </app-grid-detail-item>
        </template>
        <template v-if="creationDate">
          <app-grid-detail-item
            icon="calendar_today"
            data-locator="creation-date"
            :title="`${TRANSLATION_KEY_PREFIX}.creationDate`"
          >
            {{ creationDate }}
          </app-grid-detail-item>
        </template>
        <template v-if="expirationDate">
          <app-grid-detail-item
            icon="warning"
            data-locator="deletion-date"
            :title="`${TRANSLATION_KEY_PREFIX}.expirationDate`"
          >
            <template #icon>
              <app-material-icon name="warning" color="icon-warning" filled />
            </template>
            {{ expirationDate }}
          </app-grid-detail-item>
        </template>
      </template>

      <template #footer>
        <div class="d-flex align-center column-gap-4 mt-4">
          <AppDownloadFile
            class="d-flex flex-grow-1"
            :disabled="isLoading"
            ref="appDownloadBibleRef"
            :link="bibleURL"
            :isNewTab="false"
          >
            <template #default="{ submitForm }">
              <app-button
                class="w-100"
                data-locator="download-button"
                :disabled="isLoading"
                type="outlined"
                variant="neutral"
                @click="onDownloadFile(submitForm)"
              >
                {{ $t('common.download') }}
              </app-button>
            </template>
          </AppDownloadFile>
          <AppTooltip>
            <template #activator="{ props }">
              <app-button
                class="border"
                data-locator="share-button"
                :disabled="isLoading"
                size="large"
                type="icon"
                v-bind="props"
                @click="onShare"
              >
                <app-material-icon
                  name="share"
                  size="sm"
                />
              </app-button>
            </template>
            <span>{{ $t('common.share') }}</span>
          </AppTooltip>
        </div>
      </template>
    </app-grid-item>

    <BibleDeleteDialog
      :bible="bible"
      :mnemo="mnemo"
      v-if="isBibleDeleteDialogOpened"
      @close="isBibleDeleteDialogOpened = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { POLYOFFICE_MAX_EXPORT_SIZE } from '@/common/polyOffice/constants'
import { BiblesExport } from '@/common/polyOffice/polyOffice.model'
import { ISOToShortenedDate, ISOToShortDate } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import BibleDeleteDialog from '@/project/bibles/dialogs/BibleDeleteDialog.vue'
import { ARCHIVES_ADD_ROUTE_NAME } from '@/router/routerData'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'
import { useArchivesStore } from '@/stores/archivesStore'

const TRANSLATION_KEY_PREFIX = 'project.bibles.BibleGridItem'

export default {
  name: 'BibleGridItem',
  emits: ['export-to-imanage', 'share'],
  components: {
    AppDownloadFile,
    AppTooltip,
    BibleDeleteDialog,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      isBibleDeleteDialogOpened: false,
      isLoading: false,
    }
  },
  props: {
    bible: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    autoDownload: {
      type: Boolean,
    },
    isSelected: {
      type: Boolean,
    },
  },
  computed: {
    ...mapState('bibles', ['exportBibleToImanagePending']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('user', ['isArchivingEnabled', 'polyOfficeEnabled']),
    archiveAddRouteOptions () {
      return {
        state: {
          bibles: [this.bible],
        },
        query: {
          bibleIds: this.bible.id,
          fromMnemo: this.mnemo,
        },
      }
    },
    bibleURL () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/bible/${this.bible.id}`
    },
    creationDate () {
      return ISOToShortenedDate(this.bible.generatedAt)
    },
    displayBibleRenderingPercentage() {
      return this.bible.renderProgress + '%' || ''
    },
    expirationDate () {
      return ISOToShortDate(this.bible.deleteAt)
    },
    filesInfo () {
      return this.$t(`${TRANSLATION_KEY_PREFIX}.filesInfoValue`, { 'count': this.bible.nbItems, 'size': this.bible.size})
    },
    isRendering () {
      return this.bible.status === 'processing' && !this.isRenderingComplete
    },
    isRenderingComplete () {
      return this.bible.renderProgress === 100 && this.expirationDate
    },
    menuItems() {
      const menuItems = []

      menuItems.push({
        disabled: this.isRendering,
        icon: 'download',
        id: 'download',
        title: this.$t(`common.download`),
        locatorValue: 'download-menu-item',
        action: () => this.onDownloadFile(),
      },
      {
        disabled: this.isRendering,
        icon: 'share',
        id: 'share',
        title: this.$t(`common.share`),
        locatorValue: 'share-menu-item',
        action: () => this.onShare(),
      },
      {
        title: "separator",
      })

      menuItems.push({
        disabled: this.isRendering,
        featureTag: this.isArchivingEnabled ? null : 'upgrade',
        icon: 'inventory_2',
        id: 'archive',
        title: this.$t(`common.archivesAdd`),
        locatorValue: 'archive-menu-item',
        action: () => this.onArchiveAdd(),
      })

      if (this.imanageEnabledAndLogged) {
        menuItems.push({
          disabled: this.isRendering,
          id: 'export-to-imanage',
          iconComponent: 'app-icon-imanage',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToImanage`),
          locatorValue: 'imanage-export-menu-item',
          action: () => this.onExportToImanage(),
        })
      }

      if (this.polyOfficeEnabled) {
        menuItems.push({
          disabled: this.isRendering || this.bible.size === null || this.bible.size > POLYOFFICE_MAX_EXPORT_SIZE,
          id: 'export-to-poly-office',
          iconComponent: 'app-icon-poly-office',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToPolyOffice`),
          locatorValue: 'poly-export-menu-item',
          action: () => this.onExportToPolyOffice(),
        })
      }

      menuItems.push(
      {
        title: "separator",
      },
      {
        icon: 'delete',
        iconColor: 'icon-danger',
        id: 'delete',
        style: 'text-error',
        title: this.$t(`common.delete`),
        locatorValue: 'delete-menu-item',
        action: () => this.onDelete(),
      })

      return menuItems
    },
  },
  mounted () {
    if (this.autoDownload) {
      this.onDownloadFile()
    }
  },
  methods: {
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    downloadFile (callback) {
      this.isLoading = true

      try {
        callback()
      } catch (error) {
        console.error(error)
      } finally {
        setTimeout(() => { this.isLoading = false }, 5000)
      }
    },
    onArchiveAdd () {
      if (!this.isArchivingEnabled) {
        this.archivesStore.setDiscoverElectronicSafeDialogIsOpen(true)
      } else {
        this.$router.push({
          name: ARCHIVES_ADD_ROUTE_NAME,
          ...this.archiveAddRouteOptions,
        })
      }
    },
    onDelete () {
      this.isBibleDeleteDialogOpened = true
    },
    onDownloadFile () {
      this.$refs.appDownloadBibleRef.submitForm()
    },
    onExportToImanage () {
      this.$emit('export-to-imanage', this.bible.id)
    },
    onExportToPolyOffice () {
      this.polyOfficeOpenDialogHandler()
    },
    onShare () {
      this.$emit('share', this.bible)
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new BiblesExport(
        this.mnemo,
        [this.bible.id],
      )
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToPolyOfficeSuccess`),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
  },
}
</script>
